import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Gamepad2, Trophy, Users, Zap, ChevronDown, Play, Star, Target, Flame, Crown, ArrowRight } from 'lucide-react';

// Custom scrollbar styles for Fan Zone
const scrollbarStyles = `
  .fanzone-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 228, 0, 0.3) transparent;
  }
  
  .fanzone-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  
  .fanzone-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .fanzone-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(255, 228, 0, 0.4) 0%, rgba(189, 255, 0, 0.2) 100%);
    border-radius: 3px;
  }
  
  .fanzone-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, rgba(255, 228, 0, 0.6) 0%, rgba(189, 255, 0, 0.4) 100%);
  }
  
  .fanzone-hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  .fanzone-hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;

const FanZoneIntro = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const containerRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });
  
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  // Inject custom scrollbar styles and apply to body
  useEffect(() => {
    // Create style element
    const styleEl = document.createElement('style');
    styleEl.textContent = scrollbarStyles;
    document.head.appendChild(styleEl);
    
    // Apply scrollbar class to html/body
    document.documentElement.classList.add('fanzone-scrollbar');
    document.body.classList.add('fanzone-scrollbar');
    
    return () => {
      // Cleanup on unmount
      document.head.removeChild(styleEl);
      document.documentElement.classList.remove('fanzone-scrollbar');
      document.body.classList.remove('fanzone-scrollbar');
    };
  }, []);

  // Track mouse position for interactive effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      setMousePosition({
        x: (clientX / innerWidth - 0.5) * 2,
        y: (clientY / innerHeight - 0.5) * 2
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const features = [
    {
      icon: Gamepad2,
      title: "Jeux Exclusifs",
      description: "Quiz, Memory, Puzzle et bien plus",
      color: "#FFE400"
    },
    {
      icon: Trophy,
      title: "Classements",
      description: "Affrontez les autres supporters",
      color: "#BDFF00"
    },
    {
      icon: Target,
      title: "Défis Quotidiens",
      description: "De nouveaux challenges chaque jour",
      color: "#FFE400"
    },
    {
      icon: Crown,
      title: "Badges Exclusifs",
      description: "Collectionnez et montrez vos succès",
      color: "#BDFF00"
    }
  ];

  const floatingElements = [
    { icon: "⚽", delay: 0, duration: 6, x: 15, y: 20 },
    { icon: "🏆", delay: 1, duration: 7, x: 80, y: 15 },
    { icon: "⭐", delay: 2, duration: 5, x: 70, y: 70 },
    { icon: "🎮", delay: 0.5, duration: 8, x: 20, y: 75 },
    { icon: "🔥", delay: 1.5, duration: 6, x: 85, y: 45 },
    { icon: "💪", delay: 2.5, duration: 7, x: 10, y: 50 },
  ];

  return (
    <div 
      ref={containerRef}
      className="relative min-h-screen overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 25%, #0a1628 50%, #091320 75%, #050a12 100%)' }}
    >
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            x: mousePosition.x * 30,
            y: mousePosition.y * 30,
          }}
          transition={{ type: "spring", damping: 50, stiffness: 100 }}
          className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,228,0,0.15) 0%, transparent 70%)',
            filter: 'blur(40px)'
          }}
        />
        <motion.div
          animate={{
            x: mousePosition.x * -20,
            y: mousePosition.y * -20,
          }}
          transition={{ type: "spring", damping: 50, stiffness: 100 }}
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(189,255,0,0.12) 0%, transparent 70%)',
            filter: 'blur(40px)'
          }}
        />
        <motion.div
          animate={{
            x: mousePosition.x * 15,
            y: mousePosition.y * -15,
          }}
          transition={{ type: "spring", damping: 50, stiffness: 100 }}
          className="absolute top-1/2 right-1/3 w-[400px] h-[400px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0,27,86,0.3) 0%, transparent 70%)',
            filter: 'blur(30px)'
          }}
        />
      </div>

      {/* Floating decorative elements */}
      {floatingElements.map((el, index) => (
        <motion.div
          key={index}
          className="absolute text-4xl md:text-5xl opacity-20 pointer-events-none select-none"
          style={{ left: `${el.x}%`, top: `${el.y}%` }}
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: el.duration,
            delay: el.delay,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          {el.icon}
        </motion.div>
      ))}

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,228,0,0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,228,0,0.5) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Hero Section */}
        <motion.section 
          style={{ opacity }}
          className="flex-1 flex flex-col items-center justify-center px-4 pt-24 pb-16"
        >
          <motion.div
            style={{ y: textY }}
            className="text-center max-w-5xl mx-auto"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full border border-[#FFE400]/30 bg-[#FFE400]/5"
            >
              <Flame className="w-4 h-4 text-[#FFE400]" />
              <span className="text-[#FFE400] text-sm font-medium tracking-wide">ESPACE SUPPORTERS</span>
            </motion.div>

            {/* Main title with glitch effect */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.8, type: "spring" }}
              className="relative mb-6"
            >
              <h1 className="text-7xl md:text-9xl font-black tracking-tighter">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-white/80">
                  FAN
                </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFE400] via-[#BDFF00] to-[#FFE400]">
                  ZONE
                </span>
              </h1>
              
              {/* Decorative line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-[#FFE400] to-transparent"
              />
            </motion.div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-xl md:text-2xl text-white/60 font-light max-w-2xl mx-auto mb-12 leading-relaxed"
            >
              Entrez dans l'arène des supporters de l'
              <span className="text-[#FFE400] font-medium">Entente Feignies Aulnoye</span>.
              <br />
              Jouez, défiez, dominez.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(255,228,0,0.4)" }}
                whileTap={{ scale: 0.95 }}
                onHoverStart={() => setIsHovering(true)}
                onHoverEnd={() => setIsHovering(false)}
                onClick={() => currentUser ? navigate('/fanzone/dashboard') : navigate('/auth')}
                className="group relative px-10 py-5 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-2xl overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#BDFF00] to-[#FFE400] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative flex items-center gap-3">
                  <Play className="w-5 h-5 text-[#001B56] fill-current" />
                  <span className="text-[#001B56] font-bold text-lg">
                    {currentUser ? "Entrer dans l'arène" : "Rejoindre la zone"}
                  </span>
                  <ArrowRight className="w-5 h-5 text-[#001B56] group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.button>

              {!currentUser && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/auth')}
                  className="px-8 py-4 border border-white/20 rounded-2xl text-white/80 hover:text-white hover:border-white/40 hover:bg-white/5 transition-all duration-300"
                >
                  <span className="font-medium">Se connecter</span>
                </motion.button>
              )}
            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="flex flex-col items-center gap-2 text-white/40"
            >
              <span className="text-xs uppercase tracking-widest">Découvrir</span>
              <ChevronDown className="w-5 h-5" />
            </motion.div>
          </motion.div>
        </motion.section>

        {/* Features Section */}
        <section className="relative py-24 px-4">
          <div className="max-w-6xl mx-auto">
            {/* Section header */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              className="text-center mb-20"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Votre terrain de jeu
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                Des expériences conçues pour les vrais supporters
              </p>
            </motion.div>

            {/* Features grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -5 }}
                  className="group relative p-8 rounded-3xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm overflow-hidden cursor-pointer"
                >
                  {/* Hover glow effect */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: `radial-gradient(circle at 50% 0%, ${feature.color}10 0%, transparent 70%)`
                    }}
                  />
                  
                  {/* Content */}
                  <div className="relative z-10">
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                      style={{ 
                        background: `linear-gradient(135deg, ${feature.color}15, ${feature.color}05)`,
                        border: `1px solid ${feature.color}20`
                      }}
                    >
                      <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#FFE400] transition-colors">
                      {feature.title}
                    </h3>
                    
                    <p className="text-white/50 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>

                  {/* Corner accent */}
                  <div 
                    className="absolute top-0 right-0 w-24 h-24 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: `linear-gradient(225deg, ${feature.color}08 0%, transparent 70%)`
                    }}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="relative py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative p-12 rounded-[2rem] overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(255,228,0,0.05) 0%, rgba(189,255,0,0.02) 100%)',
                border: '1px solid rgba(255,228,0,0.1)'
              }}
            >
              {/* Background pattern */}
              <div 
                className="absolute inset-0 opacity-[0.03]"
                style={{
                  backgroundImage: 'radial-gradient(circle at 2px 2px, #FFE400 1px, transparent 1px)',
                  backgroundSize: '32px 32px'
                }}
              />

              <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                  { value: "500+", label: "Joueurs actifs", icon: Users },
                  { value: "10K+", label: "Parties jouées", icon: Gamepad2 },
                  { value: "50+", label: "Badges à débloquer", icon: Trophy },
                  { value: "24/7", label: "Compétition", icon: Zap }
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    <stat.icon className="w-6 h-6 text-[#FFE400]/60 mx-auto mb-3" />
                    <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                      {stat.value}
                    </div>
                    <div className="text-white/40 text-sm">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative py-24 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Prêt à relever le
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFE400] to-[#BDFF00]"> défi</span> ?
              </h2>
              
              <p className="text-white/50 text-lg mb-10 max-w-xl mx-auto">
                Rejoignez des centaines de supporters et prouvez que vous êtes le plus grand fan de l'EFAFC.
              </p>

              <motion.button
                whileHover={{ scale: 1.05, boxShadow: "0 0 60px rgba(255,228,0,0.3)" }}
                whileTap={{ scale: 0.95 }}
                onClick={() => currentUser ? navigate('/fanzone/dashboard') : navigate('/auth')}
                className="group relative px-12 py-6 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-2xl overflow-hidden"
              >
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                <div className="relative flex items-center gap-3">
                  <Star className="w-5 h-5 text-[#001B56]" />
                  <span className="text-[#001B56] font-bold text-xl">
                    {currentUser ? "Accéder à mes jeux" : "Commencer l'aventure"}
                  </span>
                </div>
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Footer spacer */}
        <div className="h-20" />
      </div>
    </div>
  );
};

export default FanZoneIntro;
